import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import loyaltyService from '../../../services/loyaltyService';

const WalletOverview = ({ walletData, user, onRefresh }) => {
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecentTransactions = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await loyaltyService?.getTransactionHistory(user?.id, 5);
        if (!error) {
          setRecentTransactions(data || []);
        }
      } catch (error) {
        console.error('Error loading recent transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecentTransactions();
  }, [user?.id, walletData]);

  const getTierIcon = (tier) => {
    const tierIcons = {
      'Member': 'User',
      'Bronze': 'Medal',
      'Silver': 'Award',
      'Gold': 'Crown',
      'Platinum': 'Gem'
    };
    return tierIcons?.[tier] || 'User';
  };

  const getTierColor = (tier) => {
    const tierColors = {
      'Member': 'text-muted-foreground',
      'Bronze': 'text-amber-600',
      'Silver': 'text-gray-500',
      'Gold': 'text-yellow-500',
      'Platinum': 'text-purple-500'
    };
    return tierColors?.[tier] || 'text-muted-foreground';
  };

  const getNextTierProgress = (currentTier, lifetimeEarned) => {
    const tierThresholds = {
      'Member': { next: 'Bronze', required: 1000 },
      'Bronze': { next: 'Silver', required: 2500 },
      'Silver': { next: 'Gold', required: 5000 },
      'Gold': { next: 'Platinum', required: 10000 },
      'Platinum': { next: null, required: null }
    };

    const current = tierThresholds?.[currentTier];
    if (!current?.next) return null;

    const progress = (lifetimeEarned / current?.required) * 100;
    const remaining = Math.max(0, current?.required - lifetimeEarned);

    return {
      nextTier: current?.next,
      progress: Math.min(100, progress),
      remaining,
      required: current?.required
    };
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getTransactionIcon = (type) => {
    const icons = {
      'earned_activity': 'Plus',
      'earned_goal': 'Target',
      'earned_streak': 'Flame',
      'earned_challenge': 'Trophy',
      'spent_reward': 'Minus',
      'spent_marketplace': 'ShoppingCart',
      'bonus': 'Gift',
      'refund': 'RefreshCw'
    };
    return icons?.[type] || 'Circle';
  };

  if (!walletData) {
    return (
      <div className="space-y-6">
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <Icon name="Wallet" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Wallet Not Found</h3>
          <p className="text-muted-foreground mb-4">
            We couldn't find your loyalty wallet. It may not be set up yet.
          </p>
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Initialize Wallet
          </button>
        </div>
      </div>
    );
  }

  const tierProgress = getNextTierProgress(walletData?.loyalty_tier, walletData?.lifetime_earned);

  return (
    <div className="space-y-6">
      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Coins" size={24} color="white" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Current Balance</span>
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-foreground">
              {loyaltyService?.formatTokens(walletData?.current_balance)}
            </p>
            <p className="text-sm text-muted-foreground">Available Tokens</p>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
              <Icon name="TrendingUp" size={24} color="white" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Lifetime Earned</span>
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-foreground">
              {loyaltyService?.formatTokens(walletData?.lifetime_earned)}
            </p>
            <p className="text-sm text-muted-foreground">Total Earned</p>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
              <Icon name="ShoppingBag" size={24} color="white" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Total Spent</span>
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-foreground">
              {loyaltyService?.formatTokens(walletData?.lifetime_spent)}
            </p>
            <p className="text-sm text-muted-foreground">On Rewards</p>
          </div>
        </div>
      </div>
      {/* Tier Information */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getTierColor(walletData?.loyalty_tier)} bg-muted`}>
              <Icon name={getTierIcon(walletData?.loyalty_tier)} size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{walletData?.loyalty_tier} Member</h3>
              <p className="text-sm text-muted-foreground">
                {loyaltyService?.formatTokens(walletData?.lifetime_earned)} tokens earned
              </p>
            </div>
          </div>
          
          {walletData?.pending_redemptions > 0 && (
            <div className="text-right">
              <p className="text-sm font-medium text-foreground">{walletData?.pending_redemptions}</p>
              <p className="text-sm text-muted-foreground">Pending Redemptions</p>
            </div>
          )}
        </div>

        {/* Progress to Next Tier */}
        {tierProgress && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                Progress to {tierProgress?.nextTier}
              </span>
              <span className="text-sm text-muted-foreground">
                {loyaltyService?.formatTokens(tierProgress?.remaining)} more needed
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${tierProgress?.progress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      {/* Recent Activity */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
          <button
            onClick={() => {}} // This would navigate to full history
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            View All
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)]?.map((_, i) => (
              <div key={i} className="animate-pulse flex items-center space-x-3">
                <div className="w-10 h-10 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
                <div className="w-16 h-4 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        ) : recentTransactions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Activity" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No recent transactions</p>
            <p className="text-sm text-muted-foreground mt-1">
              Complete activities to start earning tokens!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentTransactions?.map((transaction) => (
              <div key={transaction?.id} className="flex items-center space-x-3 p-3 rounded-lg border border-border">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  transaction?.amount > 0 ? 'bg-success/20' : 'bg-destructive/20'
                }`}>
                  <Icon 
                    name={getTransactionIcon(transaction?.transaction_type)} 
                    size={18} 
                    className={transaction?.amount > 0 ? 'text-success' : 'text-destructive'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {transaction?.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {loyaltyService?.getTransactionTypeLabel(transaction?.transaction_type)} • {formatTimeAgo(transaction?.created_at)}
                  </p>
                </div>
                <div className={`font-bold text-sm ${
                  transaction?.amount > 0 ? 'text-success' : 'text-destructive'
                }`}>
                  {transaction?.amount > 0 ? '+' : ''}{loyaltyService?.formatTokens(transaction?.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button className="p-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-left">
          <div className="flex items-center space-x-3">
            <Icon name="Gift" size={24} />
            <div>
              <p className="font-medium">Browse Rewards</p>
              <p className="text-sm opacity-90">Redeem your tokens</p>
            </div>
          </div>
        </button>
        
        <button className="p-4 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-left">
          <div className="flex items-center space-x-3">
            <Icon name="Plus" size={24} />
            <div>
              <p className="font-medium">Earn More Tokens</p>
              <p className="text-sm text-muted-foreground">Complete activities</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default WalletOverview;